import { useMemo, useState } from 'react';
import { Button, Card, DatePicker, Descriptions, Drawer, Input, Select, Space, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { type Dayjs } from 'dayjs';
import { CaretLeft, CaretRight, Eye } from '@phosphor-icons/react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { DataTable } from '../../components/DataTable';
import { ErrorState } from '../../components/PageStates';
import { StatusTag } from '../../components/StatusTag';
import { isSuperAdmin, useCurrentStaff } from '../../auth/session';
import { catalogApi, catalogKeys } from '../catalog/api';
import { tokens } from '../../theme';
import {
  AUDIT_ACTION_GROUPS,
  auditApi,
  auditKeys,
  type AuditActorType,
  type AuditLogFilters,
  type AuditLogItem,
} from './api';

const PAGE_SIZE = 20;

/** Loại actor → tone của chip (không mang nghĩa trạng thái, chỉ phân loại nguồn). */
const ACTOR_TONE: Record<AuditActorType, string> = {
  Staff: 'blue',
  Member: 'green',
  System: 'default',
};
const ACTOR_LABEL: Record<AuditActorType, string> = {
  Staff: 'Nhân viên',
  Member: 'Khách',
  System: 'Hệ thống',
};

function formatMoment(iso: string): string {
  // DB lưu UTC; luôn hiển thị giờ VN (CLAUDE.md nguyên tắc 6). Ghim `timeZone` chứ không dựa vào
  // timezone trình duyệt — máy đặt UTC sẽ hiện sai đúng bằng 7 tiếng nếu không ghim.
  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Asia/Ho_Chi_Minh',
  }).format(new Date(iso));
}

/** In đẹp JSONB; giữ nguyên văn nếu không phải JSON hợp lệ (một số dòng ghi chuỗi thô). */
function prettyJson(raw: string | null): string {
  if (raw === null) return '—';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function AuditLogsPage() {
  const staff = useCurrentStaff();
  const hq = isSuperAdmin(staff);

  const [groupValue, setGroupValue] = useState('all');
  const [actorType, setActorType] = useState<AuditActorType | 'all'>('all');
  const [entity, setEntity] = useState('');
  const [branchId, setBranchId] = useState('all');
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [selected, setSelected] = useState<AuditLogItem | null>(null);

  // Con trỏ keyset: một ngăn xếp con trỏ để đi tới/lui mà không đếm tổng (giống `a-recon`).
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const resetPaging = () => {
    setCursorStack([null]);
    setPageIndex(0);
  };

  const branchesQuery = useQuery({
    queryKey: catalogKeys.branches,
    queryFn: catalogApi.listBranches,
    enabled: hq,
  });

  const filters: AuditLogFilters = useMemo(() => {
    const group = AUDIT_ACTION_GROUPS.find((g) => g.value === groupValue);
    return {
      actorType: actorType === 'all' ? undefined : actorType,
      actionPrefixes: group && group.prefixes.length > 0 ? group.prefixes : undefined,
      entity: entity.trim() || undefined,
      branchId: hq && branchId !== 'all' ? branchId : undefined,
      from: range ? range[0].format('YYYY-MM-DD') : undefined,
      to: range ? range[1].format('YYYY-MM-DD') : undefined,
    };
  }, [groupValue, actorType, entity, branchId, range, hq]);

  const currentCursor = cursorStack[pageIndex];
  const logsQuery = useQuery({
    // `pageSize` vào key: đổi số dòng hiển thị là một tập kết quả khác, phải refetch.
    queryKey: [...auditKeys.list(filters, currentCursor), pageSize],
    queryFn: () => auditApi.list(filters, currentCursor, pageSize),
    placeholderData: keepPreviousData,
  });

  // Đổi bộ lọc thì luôn về trang đầu — con trỏ cũ thuộc một tập kết quả khác.
  const onFilterChange = (apply: () => void) => {
    apply();
    resetPaging();
  };

  const branchOptions = [
    { value: 'all', label: 'Mọi chi nhánh' },
    ...(branchesQuery.data ?? []).map((b) => ({ value: b.id, label: b.name })),
  ];

  const columns: ColumnsType<AuditLogItem> = [
    {
      title: 'Thời gian',
      key: 'createdAt',
      width: 172,
      render: (_, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5 }}>
          {formatMoment(row.createdAt)}
        </span>
      ),
    },
    {
      title: 'Người thực hiện',
      key: 'actorName',
      width: 200,
      render: (_, row) => (
        <Space size={6}>
          <StatusTag color={ACTOR_TONE[row.actorType]}>{ACTOR_LABEL[row.actorType]}</StatusTag>
          <span>{row.actorName ?? <span style={{ color: tokens.color.ink3 }}>—</span>}</span>
        </Space>
      ),
    },
    {
      title: 'Thao tác',
      dataIndex: 'action',
      key: 'action',
      render: (value: string) => (
        <code
          style={{
            fontSize: 12.5,
            background: tokens.color.surface2,
            padding: '1px 6px',
            borderRadius: tokens.radius.control,
          }}
        >
          {value}
        </code>
      ),
    },
    {
      title: 'Đối tượng',
      key: 'entity',
      width: 200,
      render: (_, row) => (
        <>
          <div style={{ fontWeight: 600, fontSize: 12.5 }}>{row.entity}</div>
          {row.entityId && (
            <div style={{ fontSize: 11, color: tokens.color.ink3, fontVariantNumeric: 'tabular-nums' }}>
              {row.entityId.slice(0, 8)}…
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Chi nhánh',
      key: 'branchName',
      width: 160,
      render: (_, row) =>
        row.branchName ?? <span style={{ color: tokens.color.ink3 }}>Toàn hệ thống</span>,
    },
    {
      title: 'Chi tiết',
      key: 'detail',
      width: 84,
      align: 'center',
      render: (_, row) => (
        <Button
          type="text"
          size="small"
          icon={<Eye size={16} aria-hidden />}
          onClick={() => setSelected(row)}
          aria-label="Xem before/after"
        />
      ),
    },
  ];

  const items = logsQuery.data?.items ?? [];
  const hasNext = Boolean(logsQuery.data?.nextCursor);

  return (
    <>
      <PageHeader
        title="Nhật ký"
        subtitle="Mọi thay đổi dữ liệu đều để lại vết: ai làm, làm gì, trước và sau ra sao, lúc nào."
        actions={
          <Space size={8} wrap>
            <Select
              aria-label="Nhóm thao tác"
              value={groupValue}
              onChange={(v) => onFilterChange(() => setGroupValue(v))}
              style={{ minWidth: 210, minHeight: 36 }}
              options={AUDIT_ACTION_GROUPS.map((g) => ({ value: g.value, label: g.label }))}
            />
            <Select
              aria-label="Loại người thực hiện"
              value={actorType}
              onChange={(v) => onFilterChange(() => setActorType(v))}
              style={{ minWidth: 150, minHeight: 36 }}
              options={[
                { value: 'all', label: 'Mọi người thực hiện' },
                { value: 'Staff', label: 'Nhân viên' },
                { value: 'Member', label: 'Khách' },
                { value: 'System', label: 'Hệ thống' },
              ]}
            />
            {hq && (
              <Select
                aria-label="Lọc chi nhánh"
                value={branchId}
                onChange={(v) => onFilterChange(() => setBranchId(v))}
                style={{ minWidth: 170, minHeight: 36 }}
                options={branchOptions}
              />
            )}
            <Input
              value={entity}
              onChange={(e) => onFilterChange(() => setEntity(e.target.value))}
              placeholder="Đối tượng (vd: SystemConfig)"
              allowClear
              aria-label="Lọc theo đối tượng"
              style={{ width: 200, minHeight: 36 }}
            />
            <DatePicker.RangePicker
              value={range}
              onChange={(v) => onFilterChange(() => setRange(v as [Dayjs, Dayjs] | null))}
              format="DD/MM/YYYY"
              allowClear
              aria-label="Khoảng ngày"
              style={{ minHeight: 36 }}
            />
          </Space>
        }
      />
      <PageBody>
        {logsQuery.isError ? (
          <ErrorState error={logsQuery.error} onRetry={() => void logsQuery.refetch()} />
        ) : (
          <>
            <Card styles={{ body: { paddingTop: 8 } }}>
              <DataTable
                rowKey="id"
                title="Nhật ký thay đổi"
                subTitle={
                  range
                    ? `${range[0].format('DD/MM/YYYY')} – ${range[1].format('DD/MM/YYYY')}`
                    : 'Toàn thời gian, mới nhất trước'
                }
                onReload={() => void logsQuery.refetch()}
                searchable={false}
                loading={logsQuery.isFetching}
                columns={columns}
                dataSource={items}
                pagination={false}
                scroll={{ x: 900 }}
                locale={{
                  emptyText: 'Không có dòng nhật ký nào khớp bộ lọc.',
                }}
              />

              {/* Phân trang keyset ở DƯỚI bảng: chọn số dòng/trang bên trái, đi Trước/Sau bên phải. */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 8,
                  marginTop: 12,
                }}
              >
                <Space size={8}>
                  <Typography.Text style={{ fontSize: 12, color: tokens.color.ink3 }}>
                    Hiển thị
                  </Typography.Text>
                  <Select
                    size="small"
                    value={pageSize}
                    onChange={(v) => {
                      setPageSize(v);
                      resetPaging(); // con trỏ cũ dựng theo cỡ trang cũ — phải về trang đầu
                    }}
                    style={{ width: 80 }}
                    options={[
                      { value: 20, label: '20' },
                      { value: 50, label: '50' },
                      { value: 100, label: '100' },
                    ]}
                  />
                  <Typography.Text style={{ fontSize: 12, color: tokens.color.ink3 }}>
                    dòng / trang
                  </Typography.Text>
                </Space>
                <Space size={4}>
                  <Button
                    size="small"
                    icon={<CaretLeft size={14} aria-hidden />}
                    disabled={pageIndex === 0}
                    onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
                  >
                    Trước
                  </Button>
                  <Typography.Text style={{ fontSize: 12, color: tokens.color.ink3 }}>
                    Trang {pageIndex + 1}
                  </Typography.Text>
                  <Button
                    size="small"
                    icon={<CaretRight size={14} aria-hidden />}
                    disabled={!hasNext}
                    onClick={() => {
                      const next = logsQuery.data?.nextCursor ?? null;
                      if (!next) return;
                      setCursorStack((s) => (pageIndex + 1 < s.length ? s : [...s, next]));
                      setPageIndex((i) => i + 1);
                    }}
                  >
                    Sau
                  </Button>
                </Space>
              </div>
            </Card>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 16 }}>
              Nhật ký chỉ ghi thêm, không ai sửa hay xóa được dòng nào. Nhóm <b>Cấu hình</b> là các lần
              đổi thông số vận hành. Quản lý chi nhánh chỉ thấy dòng của chi nhánh mình.
            </Typography.Paragraph>
          </>
        )}
      </PageBody>

      <Drawer
        title="Chi tiết dòng nhật ký"
        width={560}
        open={selected !== null}
        onClose={() => setSelected(null)}
        destroyOnClose
      >
        {selected && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Thời gian">{formatMoment(selected.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="Người thực hiện">
                <Space size={6}>
                  <StatusTag color={ACTOR_TONE[selected.actorType]}>
                    {ACTOR_LABEL[selected.actorType]}
                  </StatusTag>
                  {selected.actorName ?? '—'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Thao tác">
                <code>{selected.action}</code>
              </Descriptions.Item>
              <Descriptions.Item label="Đối tượng">
                {selected.entity}
                {selected.entityId ? ` · ${selected.entityId}` : ''}
              </Descriptions.Item>
              <Descriptions.Item label="Chi nhánh">
                {selected.branchName ?? 'Toàn hệ thống'}
              </Descriptions.Item>
              {selected.correlationId && (
                <Descriptions.Item label="Correlation Id">
                  <code style={{ fontSize: 11 }}>{selected.correlationId}</code>
                </Descriptions.Item>
              )}
            </Descriptions>

            <div>
              <Typography.Text strong style={{ fontSize: 12.5 }}>
                Trước (before)
              </Typography.Text>
              <JsonBlock value={prettyJson(selected.before)} />
            </div>
            <div>
              <Typography.Text strong style={{ fontSize: 12.5 }}>
                Sau (after)
              </Typography.Text>
              <JsonBlock value={prettyJson(selected.after)} />
            </div>
          </Space>
        )}
      </Drawer>
    </>
  );
}

function JsonBlock({ value }: { value: string }) {
  return (
    <pre
      style={{
        margin: '6px 0 0',
        padding: 12,
        background: tokens.color.surface2,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: tokens.radius.control,
        fontSize: 12,
        lineHeight: 1.5,
        maxHeight: 260,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {value}
    </pre>
  );
}
